import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Alert, Box, Button, Spinner, TextField, Typography, portalChrome } from '@glplus/glpweb-dsl';
import { answerNewPassword, finishReset, hasSession, safeNextPath, startReset, startSignIn, type LoginStage } from '../lib/authFlow';
import MainFooter from '../components/MainFooter';
import { ORANGE, ORANGE_LINK_STYLE } from '../styles/colors';

const CHROME = portalChrome.driver;
const PUBLIC_SITE = (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined) ?? 'https://www.grocerylistplus.com';

/**
 * /login — deck §1 "Runner sign-in". Runner-pool accounts only: applicants get
 * theirs at submit (invite email with a temporary password → "Create your
 * password" on first sign-in). "New here? Become a Runner" goes to the public
 * site's /drivers funnel — there is no self-signup on this portal.
 */
type Feedback = { severity: 'error' | 'warning' | 'success'; title?: string; body: string } | null;

const FIELD_LABEL_SX = { fontWeight: 600, fontSize: '13px', color: '#4b5563', mb: 0.5 } as const;
const PRIMARY_BTN_SX = { backgroundColor: ORANGE, color: '#fff', '&:hover': { backgroundColor: '#d9670f' }, width: 'fit-content', maxWidth: 'min(280px, 100%)', minHeight: 44, alignSelf: 'center', whiteSpace: 'normal', fontWeight: 700 } as const;

export default function Login() {
  const { t, i18n } = useTranslation();
  const [params] = useSearchParams();
  const next = safeNextPath(params.get('next'));
  const [checking, setChecking] = useState(true);
  const [stage, setStage] = useState<LoginStage>({ kind: 'credentials' });
  const [email, setEmail] = useState('');
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void hasSession().then((live) => {
      if (live) window.location.assign(next);
      else setChecking(false);
    });
  }, [next]);

  const run = async (fn: () => Promise<LoginStage>, onOk?: Feedback) => {
    setBusy(true);
    setFeedback(null);
    try {
      const out = await fn();
      if (out.kind === 'done') window.location.assign(next);
      else {
        setStage(out);
        if (onOk) setFeedback(onOk);
      }
    } catch (err) {
      setFeedback(mapError(err, t));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box component="main" data-testid="page-login" sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#f5f6f8' }}>
      <Box component="header" sx={{ background: CHROME.headerBg, color: '#fff', minHeight: 72, px: { xs: '16px', sm: '32px', lg: '40px' }, display: 'flex', alignItems: 'center' }}>
        {/* The Runner wordmark (docs/SVG Frame 91 / 92, 2026-10-01). Rod: the new logo on the signed-out page; the signed-in header keeps the all-white logo. */}
        <Box
          component="img"
          src={i18n.language.startsWith('es') ? '/brand/runner-wordmark-es.svg' : '/brand/runner-wordmark-en.svg'}
          alt={i18n.language.startsWith('es') ? 'Repartidor de Lista de Compras Plus' : 'Grocery List Plus Runner'}
          data-testid="login-wordmark"
          sx={{ height: 44, width: 'auto', display: 'block' }}
        />
      </Box>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', px: 2, pt: { xs: 6, md: 12 }, pb: 8 }}>
        {checking ? (
          <Spinner aria-label={t('login.checking')} />
        ) : (
          <Box sx={{ width: 360, maxWidth: '100%', backgroundColor: '#fff', borderRadius: 3, borderTop: '3px solid #343F5B', boxShadow: '0 8px 24px -4px rgba(16,24,40,0.10)', p: 3, display: 'flex', flexDirection: 'column' }}>
            {feedback && (
              <Alert severity={feedback.severity} title={feedback.title} data-testid={`login-feedback-${feedback.severity}`} sx={{ mb: 2 }}>
                {feedback.body}
              </Alert>
            )}
            {stage.kind === 'credentials' && (
              <Stage title={t('login.title')} subtitle={t('login.subtitle')}>
                <Field label={t('login.email')}>
                  <TextField ariaLabel={t('login.email')} dataTestId="login-email" size="small" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ width: '100%' }} />
                </Field>
                <PasswordStep busy={busy} dataTestId="login-password" submitTestId="login-submit" label={t('login.password')} cta={busy ? t('login.signingIn') : t('login.signIn')} onSubmit={(pw) => void run(() => startSignIn(email.trim(), pw))} ready={/.+@.+\..+/.test(email.trim())} />
                <Typography sx={{ mt: 2, fontSize: '13px', color: '#5f6b7a' }}>
                  {t('login.newHere')}{' '}
                  <Box component="a" href={`${PUBLIC_SITE}/drivers`} data-testid="login-become-runner" sx={{ ...ORANGE_LINK_STYLE, textDecoration: 'none' }}>
                    {t('login.become')}
                  </Box>
                </Typography>
                <Button ariaLabel={t('login.forgotAria')} dataTestId="login-forgot" buttonText={t('login.forgot')} variant="text" size="small" sx={{ mt: 1, alignSelf: 'center', color: '#343F5B', minHeight: 44, maxWidth: 'min(280px, 100%)' }} onClick={() => setStage({ kind: 'forgot' })} />
              </Stage>
            )}
            {stage.kind === 'newPassword' && (
              <Stage title={t('login.createTitle')} subtitle={t('login.createSub')}>
                <NewPasswordFields busy={busy} cta={t('login.continue')} onSubmit={(pw) => void run(() => answerNewPassword(pw))} />
              </Stage>
            )}
            {stage.kind === 'forgot' && (
              <Stage title={t('login.resetTitle')} subtitle={t('login.resetSub')}>
                <Field label={t('login.email')}>
                  <TextField ariaLabel={t('login.email')} size="small" type="email" value={email} onChange={(e) => setEmail(e.target.value)} sx={{ width: '100%' }} />
                </Field>
                <Button ariaLabel={t('login.sendCodeAria')} buttonText={busy ? t('login.sending') : t('login.sendCode')} variant="contained" disabled={busy || !/.+@.+\..+/.test(email.trim())} sx={{ ...PRIMARY_BTN_SX, mt: 2 }} onClick={() => void run(() => startReset(email.trim(), i18n.language.startsWith('es') ? 'es' : 'en'))} />
                <Button ariaLabel={t('login.back')} buttonText={t('login.back')} variant="text" size="small" sx={{ mt: 1, alignSelf: 'center', color: '#343F5B', minHeight: 44, maxWidth: 'min(280px, 100%)' }} onClick={() => setStage({ kind: 'credentials' })} />
              </Stage>
            )}
            {stage.kind === 'resetCode' && (
              <Stage title={t('login.checkTitle')} subtitle={t('login.checkSub', { email: stage.email })}>
                <ResetFields busy={busy} onSubmit={(code, pw) => void run(() => finishReset(stage.email, code, pw), { severity: 'success', body: t('login.updated') })} />
              </Stage>
            )}
          </Box>
        )}
      </Box>
      <MainFooter />
    </Box>
  );
}

function Stage({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
  return (
    <Box component="form" onSubmit={(e: React.FormEvent) => e.preventDefault()} sx={{ display: 'flex', flexDirection: 'column' }}>
      <Typography component="h1" sx={{ fontWeight: 800, fontSize: '20px', color: '#343F5B', m: 0 }}>{title}</Typography>
      <Typography sx={{ fontSize: '13px', color: '#5f6b7a', mt: 0.5 }}>{subtitle}</Typography>
      {children}
    </Box>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mt: 2 }}>
      <Typography sx={FIELD_LABEL_SX}>{label}</Typography>
      {children}
    </Box>
  );
}
function PasswordField({ ariaLabel, value, onChange, dataTestId }: { ariaLabel: string; value: string; onChange: (v: string) => void; dataTestId?: string }) {
  const [visible, setVisible] = useState(false);
  return <TextField ariaLabel={ariaLabel} dataTestId={dataTestId} size="small" type={visible ? 'text' : 'password'} value={value} onChange={(e) => onChange(e.target.value)} endAdornment="visibility" handleIconClick={() => setVisible((v) => !v)} sx={{ width: '100%' }} />;
}
function PasswordStep({ busy, label, cta, ready, onSubmit, dataTestId, submitTestId }: { busy: boolean; label: string; cta: string; ready: boolean; onSubmit: (pw: string) => void; dataTestId?: string; submitTestId?: string }) {
  const [pw, setPw] = useState('');
  const ok = ready && pw.length > 0;
  return (
    <>
      <Field label={label}>
        <PasswordField ariaLabel={label} dataTestId={dataTestId} value={pw} onChange={setPw} />
      </Field>
      <Button ariaLabel={cta} dataTestId={submitTestId} buttonText={cta} variant="contained" disabled={busy || !ok} sx={{ ...PRIMARY_BTN_SX, mt: 2 }} onClick={() => onSubmit(pw)} />
      <button type="submit" style={{ display: 'none' }} aria-hidden="true" onClick={() => ok && !busy && onSubmit(pw)} />
    </>
  );
}
const PASSWORD_REQUIREMENTS: Array<[string, (pw: string) => boolean]> = [
  ['login.reqLength', (pw) => pw.length >= 12],
  ['login.reqCase', (pw) => /[a-z]/.test(pw) && /[A-Z]/.test(pw)],
  ['login.reqNumber', (pw) => /\d/.test(pw)],
  ['login.reqSymbol', (pw) => /[^A-Za-z0-9]/.test(pw)],
];
function NewPasswordFields({ busy, cta, onSubmit }: { busy: boolean; cta: string; onSubmit: (pw: string) => void }) {
  const { t } = useTranslation();
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const ready = PASSWORD_REQUIREMENTS.every(([, ok]) => ok(pw)) && pw === pw2;
  return (
    <>
      <Field label={t('login.newPassword')}><PasswordField ariaLabel={t('login.newPassword')} value={pw} onChange={setPw} /></Field>
      <Field label={t('login.confirmPassword')}><PasswordField ariaLabel={t('login.confirmPassword')} value={pw2} onChange={setPw2} /></Field>
      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {PASSWORD_REQUIREMENTS.map(([label, ok]) => (
          <Typography key={label} sx={{ fontSize: '12px', color: ok(pw) ? '#027a48' : '#5f6b7a' }}>✓ {t(label)}</Typography>
        ))}
        {pw2.length > 0 && pw !== pw2 && <Typography sx={{ fontSize: '12px', color: '#b42318' }}>{t('login.mismatch')}</Typography>}
      </Box>
      <Button ariaLabel={cta} buttonText={busy ? t('login.saving') : cta} variant="contained" disabled={busy || !ready} sx={{ ...PRIMARY_BTN_SX, mt: 2 }} onClick={() => onSubmit(pw)} />
    </>
  );
}
function ResetFields({ busy, onSubmit }: { busy: boolean; onSubmit: (code: string, pw: string) => void }) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const ready = /^\d{6}$/.test(code.trim()) && PASSWORD_REQUIREMENTS.every(([, ok]) => ok(pw)) && pw === pw2;
  return (
    <>
      <Field label={t('login.code')}><TextField ariaLabel={t('login.codeAria')} size="small" value={code} onChange={(e) => setCode(e.target.value)} sx={{ width: '100%' }} /></Field>
      <Field label={t('login.newPassword')}><PasswordField ariaLabel={t('login.newPassword')} value={pw} onChange={setPw} /></Field>
      <Field label={t('login.confirmPassword')}><PasswordField ariaLabel={t('login.confirmPassword')} value={pw2} onChange={setPw2} /></Field>
      <Button ariaLabel={t('login.setNew')} buttonText={busy ? t('login.saving') : t('login.setNew')} variant="contained" disabled={busy || !ready} sx={{ ...PRIMARY_BTN_SX, mt: 2 }} onClick={() => onSubmit(code.trim(), pw)} />
    </>
  );
}
function mapError(err: unknown, t: TFunction): Feedback {
  const name = (err as { name?: string })?.name ?? '';
  const message = (err as { message?: string })?.message ?? '';
  if (name === 'UnsupportedStepError') return { severity: 'error', body: t('login.errUnsupported') };
  if (/temporary password has expired/i.test(message)) return { severity: 'warning', title: t('login.errExpiredTitle'), body: t('login.errExpiredBody') };
  if (name === 'NotAuthorizedException' || name === 'UserNotFoundException') return { severity: 'error', title: t('login.errIncorrectTitle'), body: t('login.errIncorrectBody') };
  if (name === 'CodeMismatchException' || name === 'ExpiredCodeException') return { severity: 'error', body: t('login.errCode') };
  if (name === 'LimitExceededException' || name === 'TooManyRequestsException') return { severity: 'error', body: t('login.errTooMany') };
  if (name === 'InvalidPasswordException') return { severity: 'error', body: message.replace(/^Password did not conform with policy:\s*/i, '') || t('login.errPassword') };
  return { severity: 'error', body: message || t('login.errGeneric') };
}
